import type { ImportRow } from '../types';
import { computeRowIssues } from './matchingEngine';

export interface IncompleteRowSummary {
  totalIncomplete: number;
  /** Rows with issues that need attention (from computeRowIssues). */
  withIssues: number;
  /** Rows with no date — will default to today. */
  missingAcquisitionDate: number;
  /** Rows with no status — will default to alive. */
  missingStatus: number;
}

/**
 * Check import rows for incomplete data. Skipped rows are excluded.
 *
 * Issue detection is driven entirely by computeRowIssues() so that the
 * modal counts always match the warning triangles in the review grid.
 * Date and status are counted separately because they have safe defaults
 * and are informational rather than blocking.
 *
 * Returns null if all non-skipped rows are complete.
 */
export function getIncompleteRowSummary(
  rows: ImportRow[],
): IncompleteRowSummary | null {
  const activeRows = rows.filter((r) => !r.isSkipped);

  let withIssues = 0;
  let missingAcquisitionDate = 0;
  let missingStatus = 0;
  const incompleteSet = new Set<number>();

  for (let i = 0; i < activeRows.length; i++) {
    const row = activeRows[i]!;
    const hasIssues = computeRowIssues(row).length > 0;
    const dateEmpty = !row.acquisitionDate;
    const statusEmpty = !row.status || !row.status.value;

    if (hasIssues) {
      withIssues++;
      incompleteSet.add(i);
    }
    if (dateEmpty) {
      missingAcquisitionDate++;
      incompleteSet.add(i);
    }
    if (statusEmpty) {
      missingStatus++;
      incompleteSet.add(i);
    }
  }

  if (incompleteSet.size === 0) return null;

  return {
    totalIncomplete: incompleteSet.size,
    withIssues,
    missingAcquisitionDate,
    missingStatus,
  };
}
